/**
 * Admin · Partner Registrations Console
 * ────────────────────────────────────────────────────────────
 * Shows every submitted partner registration (Advisor / DSA /
 * Associate / Agency / Sub Branch / Branch) with:
 *   · type filter tabs
 *   · "Paid only" ↔ "All" toggle
 *   · Download PDF button per row
 *   · Refresh, brand strip, keyboard-accessible controls
 *
 * Backend endpoints:
 *   GET  /api/admin/partner-registrations
 *   GET  /api/admin/partner-registrations/{id}/pdf
 */
import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Download,
  Users2,
  Briefcase,
  Building2,
  Handshake as HandshakeIcon,
  Filter,
  Phone,
  Mail,
  Inbox,
  Loader2,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { adminApi, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";

const TYPE_META = {
  advisor:    { label: "Advisor",           icon: Briefcase,   dot: "bg-blue-400" },
  dsa:        { label: "DSA",               icon: Users2,      dot: "bg-emerald-400" },
  associate:  { label: "Associate Partner", icon: HandshakeIcon, dot: "bg-violet-400" },
  agency:     { label: "Agency Partner",    icon: Building2,   dot: "bg-amber-400" },
  sub_branch: { label: "Sub Branch",        icon: Building2,   dot: "bg-sky-400" },
  branch:     { label: "Branch",            icon: Building2,   dot: "bg-fuchsia-400" },
};

const TYPE_ORDER = ["advisor", "dsa", "associate", "agency", "sub_branch", "branch"];
const PAGE_SIZE = 50;

const PAYMENT_BADGE = {
  paid:    { label: "Paid",    cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", icon: CheckCircle2 },
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-200 border-amber-500/30",       icon: Clock },
  waived:  { label: "Waived",  cls: "bg-slate-500/15 text-slate-200 border-slate-500/30",       icon: CheckCircle2 },
  na:      { label: "N/A",     cls: "bg-slate-500/15 text-slate-300 border-slate-500/30",       icon: CheckCircle2 },
};

export default function AdminRegistrations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState("all");
  const [paidOnly, setPaidOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // app_id currently downloading
  const hasToken = Boolean(tokenStorage.get());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        paid_only: paidOnly,
      };
      if (typeFilter !== "all") params.type = typeFilter;
      const { data } = await adminApi.get("/admin/partner-registrations", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("Failed to load registrations", e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, paidOnly, page]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (row) => {
    setDownloading(row.id);
    try {
      const resp = await adminApi.get(
        `/admin/partner-registrations/${row.id}/pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sahyog-registration-${row.type}-${(row.name || "applicant").replace(/\s+/g, "_")}-${row.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("PDF download failed", e);
      alert("Failed to download PDF. Please retry.");
    } finally {
      setDownloading(null);
    }
  };

  if (!hasToken) return <Navigate to="/admin" replace />;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-[#04102B] text-white" data-testid="admin-registrations-page">
      <header className="sticky top-0 z-30 backdrop-blur bg-[#04102B]/90 border-b border-[#D4AF37]/25">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[140px] flex items-center justify-between gap-4">
          <button
            data-testid="reg-back-home"
            onClick={() => navigate("/admin/leads")}
            className="flex items-center gap-4 text-left"
          >
            <Logo size={72} />
            <div className="leading-tight">
              <div className="font-display text-sm md:text-lg uppercase tracking-tight font-semibold whitespace-nowrap">
                SAHYOG FINANCE
                <br />
                <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
              </div>
              <div className="font-display text-[#D4AF37] text-[10px] md:text-xs tracking-[0.18em] uppercase mt-1">
                Asset Management Pvt. Ltd. · Admin Console · Registrations
              </div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="reg-refresh"
              onClick={load}
              disabled={loading}
              className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 text-base font-semibold"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <a
              href="/admin/change-password"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-base font-semibold"
            >
              Change password
            </a>
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-7xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
            · Partner Registrations ·
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">
            Every partner registration in one place
          </h1>
          <p className="mt-2 text-white/60 max-w-3xl text-sm">
            List of every Advisor / DSA / Associate / Agency / Sub Branch / Branch
            application submitted from the public site. Download the branded PDF summary
            for offline reference or physical filing.
          </p>
        </div>

        {/* Filter bar */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider">
            <Filter size={13} /> Filter
          </div>
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Registration type">
            {["all", ...TYPE_ORDER].map((t) => {
              const meta = t === "all" ? { label: "All types", dot: "bg-white/40" } : TYPE_META[t];
              const active = typeFilter === t;
              return (
                <button
                  key={t}
                  role="tab"
                  aria-selected={active}
                  data-testid={`reg-filter-${t}`}
                  onClick={() => { setPage(0); setTypeFilter(t); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition min-h-[44px] ${
                    active
                      ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F0D788]"
                      : "border-white/10 text-white/70 hover:border-[#D4AF37]/40"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                  {meta.label}
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-white/80 cursor-pointer">
              <input
                type="checkbox"
                data-testid="reg-paid-only"
                checked={paidOnly}
                onChange={(e) => { setPage(0); setPaidOnly(e.target.checked); }}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              Paid only
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#0A1F44] text-[10px] uppercase tracking-[0.22em] text-white/80">
                <tr>
                  <th className="px-4 py-3 text-left">Applicant</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Contact</th>
                  <th className="px-4 py-3 text-left">Submitted</th>
                  <th className="px-4 py-3 text-left">Payment</th>
                  <th className="px-4 py-3 text-right">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-white/70">
                      <Loader2 className="inline animate-spin mr-2" size={16} /> Loading registrations…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center text-white/70">
                      <Inbox className="inline mr-2" size={16} /> No registrations match the current filter.
                    </td>
                  </tr>
                )}
                {!loading && items.map((row) => {
                  const meta = TYPE_META[row.type] || { label: row.type, icon: Users2, dot: "bg-white/40" };
                  const IconT = meta.icon;
                  const pay = PAYMENT_BADGE[row.payment_status] || PAYMENT_BADGE.pending;
                  const PayIcon = pay.icon;
                  const submitted = (row.created_at || "").slice(0, 19).replace("T", " ");
                  return (
                    <tr key={row.id} data-testid={`reg-row-${row.id}`} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{row.name}</div>
                        <div className="text-[11px] text-white/45 mt-0.5">ID: {row.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          <IconT size={13} className="text-white/60" />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-white/70">
                        <div className="inline-flex items-center gap-1.5">
                          <Phone size={11} className="text-[#D4AF37]" /> {row.phone}
                        </div>
                        {row.email && (
                          <div className="inline-flex items-center gap-1.5 mt-1">
                            <Mail size={11} className="text-[#D4AF37]" /> {row.email}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/60 whitespace-nowrap">
                        {submitted}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${pay.cls}`}>
                          <PayIcon size={11} /> {pay.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`reg-download-${row.id}`}
                          onClick={() => downloadPdf(row)}
                          disabled={downloading === row.id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full btn-gold text-xs font-semibold min-h-[36px] disabled:opacity-60"
                        >
                          {downloading === row.id ? (
                            <><Loader2 size={12} className="animate-spin" /> …</>
                          ) : (
                            <><Download size={12} /> PDF</>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs text-white/60">
            <span>Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
            <div className="flex items-center gap-2">
              <button
                data-testid="reg-prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-4 py-2 rounded-full border border-white/10 disabled:opacity-30 min-h-[44px]"
              >
                Previous
              </button>
              <span>{page + 1} / {totalPages}</span>
              <button
                data-testid="reg-next"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-4 py-2 rounded-full border border-white/10 disabled:opacity-30 min-h-[44px]"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
